import React, { useState } from 'react';
import { Instagram, Mail, Phone, Copy, Check, ArrowUpRight, MessageCircle } from 'lucide-react';
import { contacts } from '../mock';

function DiscordIcon({ size = 18 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="12" r="1" />
      <circle cx="15" cy="12" r="1" />
      <path d="M7.5 7.2A15 15 0 0 1 12 6c1.6 0 3.1.2 4.5.6" />
      <path d="M16.5 16.8A15 15 0 0 1 12 18c-1.6 0-3.1-.2-4.5-.6" />
      <path d="M15.5 17.5c.3 1 .8 2 1.5 2.5 2.5-.8 4-3 4-5.5 0-3.5-1.5-7-3.5-9-1 .3-2 .8-2.5 1.5" />
      <path d="M8.5 17.5c-.3 1-.8 2-1.5 2.5C4.5 19.2 3 17 3 14.5c0-3.5 1.5-7 3.5-9 1 .3 2 .8 2.5 1.5" />
    </svg>
  );
}

function IconFor({ net, size = 18 }) {
  if (net === 'instagram') return <Instagram size={size} strokeWidth={1.6} />;
  if (net === 'discord')   return <DiscordIcon size={size} />;
  if (net === 'email')     return <Mail size={size} strokeWidth={1.6} />;
  if (net === 'phone')     return <Phone size={size} strokeWidth={1.6} />;
  return <MessageCircle size={size} strokeWidth={1.6} />;
}

export default function ContactSection() {
  const [copied, setCopied] = useState(null);

  const copy = (text, id, e) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1400);
  };

  const openChat = () => {
    window.dispatchEvent(new CustomEvent('open-chat'));
  };

  const handleClick = (c) => {
    if (c.network === 'email')      window.location.href = c.url;
    else if (c.network === 'phone') window.location.href = c.url;
    else if (c.network === 'instagram') window.open(c.url, '_blank');
    else if (c.network === 'discord') copy(c.handle, c.id);
  };

  return (
    <section id="contact" className="section">
      <div className="twocol"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 72,
        }}
      >
        <div className="reveal">
          <div className="section-label" style={{ marginBottom: 16 }}>
            07 &mdash; Contact
          </div>
          <h2 className="section-title" style={{ margin: 0, maxWidth: 540 }}>
            Let&apos;s make<br /> something<br />
            <span style={{ color: 'rgba(245,245,245,0.45)' }}>quietly loud.</span>
          </h2>
          <p
            style={{
              marginTop: 24,
              color: 'rgba(245,245,245,0.6)',
              fontSize: 15,
              lineHeight: 1.7,
              maxWidth: 460,
            }}
          >
            Best way to reach me is Instagram or Discord. Email works too. Or use the
            live chat &mdash; messages stay between us, and once we are done I&apos;ll wipe the thread.
          </p>

          <div style={{ marginTop: 32 }}>
            {contacts.map((c) => (
              <div
                key={c.id}
                className="contact-row"
                onClick={() => handleClick(c)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 999,
                      border: '1px solid rgba(245,245,245,0.18)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#f5f5f5',
                      flexShrink: 0,
                    }}
                  >
                    <IconFor net={c.network} />
                  </div>
                  <div>
                    <div
                      className="mono"
                      style={{
                        fontSize: 10,
                        letterSpacing: '0.3em',
                        color: 'rgba(245,245,245,0.45)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {c.label}
                    </div>
                    <div
                      style={{
                        fontFamily: 'Space Grotesk, sans-serif',
                        fontSize: 20,
                        fontWeight: 500,
                        marginTop: 2,
                      }}
                    >
                      {c.handle}
                    </div>
                    {c.note && (
                      <div
                        className="mono"
                        style={{
                          marginTop: 6,
                          fontSize: 10,
                          letterSpacing: '0.15em',
                          color: 'rgba(245,245,245,0.4)',
                          textTransform: 'none',
                          maxWidth: 360,
                        }}
                      >
                        {c.note}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  onClick={(e) => copy(c.handle, c.id, e)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    color: 'rgba(245,245,245,0.5)',
                    flexShrink: 0,
                  }}
                >
                  {copied === c.id ? (
                    <>
                      <Check size={14} /> <span className="mono" style={{ fontSize: 10, letterSpacing: '0.3em' }}>COPIED</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} /> <span className="mono" style={{ fontSize: 10, letterSpacing: '0.3em' }}>COPY</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live chat panel preview */}
        <div
          className="glass-card reveal"
          style={{ padding: 36, display: 'flex', flexDirection: 'column', gap: 20 }}
        >
          <div
            className="mono"
            style={{
              fontSize: 10,
              letterSpacing: '0.35em',
              color: 'rgba(245,245,245,0.45)',
              textTransform: 'uppercase',
            }}
          >
            / Live chat
          </div>

          <h3
            style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontWeight: 500,
              fontSize: 28,
              margin: 0,
              letterSpacing: '-0.01em',
              color: '#f5f5f5',
            }}
          >
            Send a transmission.
          </h3>
          <p style={{ color: 'rgba(245,245,245,0.65)', fontSize: 14, lineHeight: 1.7, margin: 0 }}>
            Drop a message and leave &mdash; I&apos;ll reply in the same thread when I see it.
            When the conversation is done I close the chat and the entire thread is wiped
            forever. No history, no traces.
          </p>

          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              color: 'rgba(245,245,245,0.7)',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            <li style={{ display: 'flex', gap: 10 }}>
              <Check size={14} style={{ marginTop: 3, color: '#f5f5f5' }} /> Real-time replies (when I&apos;m online).
            </li>
            <li style={{ display: 'flex', gap: 10 }}>
              <Check size={14} style={{ marginTop: 3, color: '#f5f5f5' }} /> Same thread &mdash; no email tag, no waiting.
            </li>
            <li style={{ display: 'flex', gap: 10 }}>
              <Check size={14} style={{ marginTop: 3, color: '#f5f5f5' }} /> Thread is permanently deleted on close.
            </li>
          </ul>

          <button
            className="btn-solid"
            onClick={openChat}
            style={{
              marginTop: 8,
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            Open chat <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
